import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { openDB } from 'idb';

export type EmulatorSaveType = 'auto' | 'manual';
export type EmulatorSavePayloadKind = 'console-state' | 'dos-changes';

export interface SaveQueueEntry {
  id: string;
  userId: string;
  romId: string;
  romTitle: string;
  systemId: string;
  type: EmulatorSaveType;
  notes: string | null;
  createdAt: string;
  sizeBytes: number;
  payloadKind: EmulatorSavePayloadKind;
  storagePath: string;
  screenshotPath: string | null;
  stateBuffer: ArrayBuffer;
  screenshotBuffer: ArrayBuffer | null;
}

interface UseSaveQueueOptions {
  enabled?: boolean;
  syncEntry?: (entry: SaveQueueEntry) => Promise<void>;
  syncIntervalMs?: number;
}

const BROWSER_DB_NAME = 'MapleOS-emulator-save-queue';
const BROWSER_STORE_NAME = 'pending-saves';
const ELECTRON_QUEUE_DIR = 'C:\\System\\EmulatorSaveQueue';
const DEFAULT_SYNC_INTERVAL_MS = 45_000;

type PersistedElectronSaveQueueEntry = Omit<SaveQueueEntry, 'stateBuffer' | 'screenshotBuffer'> & {
  stateBase64: string;
  screenshotBase64: string | null;
};

const getBrowserDb = () =>
  openDB(BROWSER_DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(BROWSER_STORE_NAME)) {
        db.createObjectStore(BROWSER_STORE_NAME, { keyPath: 'id' });
      }
    },
  });

const sortEntries = (entries: SaveQueueEntry[]) =>
  [...entries].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

const isElectronEnvironment = () =>
  typeof window !== 'undefined' && Boolean((window as any).electronAPI?.isElectron);

const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;

  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }

  return btoa(binary);
};

const base64ToArrayBuffer = (value: string) => {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes.buffer;
};

const toElectronPayload = (entry: SaveQueueEntry): PersistedElectronSaveQueueEntry => ({
  ...entry,
  stateBase64: arrayBufferToBase64(entry.stateBuffer),
  screenshotBase64: entry.screenshotBuffer ? arrayBufferToBase64(entry.screenshotBuffer) : null,
});

const fromElectronPayload = (entry: PersistedElectronSaveQueueEntry): SaveQueueEntry => ({
  id: entry.id,
  userId: entry.userId,
  romId: entry.romId,
  romTitle: entry.romTitle,
  systemId: entry.systemId,
  type: entry.type,
  notes: entry.notes,
  createdAt: entry.createdAt,
  sizeBytes: entry.sizeBytes,
  payloadKind: entry.payloadKind,
  storagePath: entry.storagePath,
  screenshotPath: entry.screenshotPath,
  stateBuffer: base64ToArrayBuffer(entry.stateBase64),
  screenshotBuffer: entry.screenshotBase64 ? base64ToArrayBuffer(entry.screenshotBase64) : null,
});

const electronFilePathForEntry = (entryId: string) => `${ELECTRON_QUEUE_DIR}\\${entryId}.json`;

export const useSaveQueue = ({
  enabled = true,
  syncEntry,
  syncIntervalMs = DEFAULT_SYNC_INTERVAL_MS,
}: UseSaveQueueOptions = {}) => {
  const [entries, setEntries] = useState<SaveQueueEntry[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const syncInFlightRef = useRef(false);

  const loadEntries = useCallback(async () => {
    if (!enabled) {
      setEntries([]);
      setIsLoaded(true);
      return [] as SaveQueueEntry[];
    }

    if (isElectronEnvironment()) {
      const electronApi = (window as any).electronAPI;
      await electronApi.createDirectory(ELECTRON_QUEUE_DIR);
      const filePaths = await electronApi.listFiles(ELECTRON_QUEUE_DIR);
      const parsedEntries = await Promise.all(
        filePaths
          .filter((filePath: string) => filePath.toLowerCase().endsWith('.json'))
          .map(async (filePath: string) => {
            const raw = await electronApi.readFile(filePath);
            if (!raw) {
              return null;
            }

            return fromElectronPayload(JSON.parse(raw) as PersistedElectronSaveQueueEntry);
          })
      );

      const nextEntries = sortEntries(parsedEntries.filter((entry): entry is SaveQueueEntry => Boolean(entry)));
      setEntries(nextEntries);
      setIsLoaded(true);
      return nextEntries;
    }

    const db = await getBrowserDb();
    const nextEntries = sortEntries((await db.getAll(BROWSER_STORE_NAME)) as SaveQueueEntry[]);
    setEntries(nextEntries);
    setIsLoaded(true);
    return nextEntries;
  }, [enabled]);

  const persistEntry = useCallback(async (entry: SaveQueueEntry) => {
    if (isElectronEnvironment()) {
      const electronApi = (window as any).electronAPI;
      await electronApi.createDirectory(ELECTRON_QUEUE_DIR);
      await electronApi.writeFile(electronFilePathForEntry(entry.id), JSON.stringify(toElectronPayload(entry)));
      return;
    }

    const db = await getBrowserDb();
    await db.put(BROWSER_STORE_NAME, entry);
  }, []);

  const deletePersistedEntry = useCallback(async (entryId: string) => {
    if (isElectronEnvironment()) {
      const electronApi = (window as any).electronAPI;
      const filePath = electronFilePathForEntry(entryId);
      const exists = await electronApi.exists(filePath);
      if (exists) {
        await electronApi.deleteFile(filePath);
      }
      return;
    }

    const db = await getBrowserDb();
    await db.delete(BROWSER_STORE_NAME, entryId);
  }, []);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  const enqueue = useCallback(
    async (entry: SaveQueueEntry) => {
      await persistEntry(entry);
      setEntries((current) => sortEntries([...current.filter((item) => item.id !== entry.id), entry]));
      return entry;
    },
    [persistEntry]
  );

  const remove = useCallback(
    async (entryId: string) => {
      await deletePersistedEntry(entryId);
      setEntries((current) => current.filter((entry) => entry.id !== entryId));
    },
    [deletePersistedEntry]
  );

  const getEntry = useCallback(
    async (entryId: string) => {
      const existing = entries.find((entry) => entry.id === entryId);
      if (existing) {
        return existing;
      }

      const loadedEntries = await loadEntries();
      return loadedEntries.find((entry) => entry.id === entryId) ?? null;
    },
    [entries, loadEntries]
  );

  const syncPending = useCallback(async () => {
    if (!enabled || !syncEntry || syncInFlightRef.current) {
      return;
    }

    if (typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine) {
      return;
    }

    syncInFlightRef.current = true;
    setIsSyncing(true);

    try {
      const currentEntries = entries.length > 0 ? [...entries] : await loadEntries();
      const oldestFirst = [...currentEntries].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );

      for (const entry of oldestFirst) {
        await syncEntry(entry);
        await deletePersistedEntry(entry.id);
        setEntries((current) => current.filter((item) => item.id !== entry.id));
      }
    } finally {
      syncInFlightRef.current = false;
      setIsSyncing(false);
    }
  }, [deletePersistedEntry, enabled, entries, loadEntries, syncEntry]);

  useEffect(() => {
    if (!enabled || !syncEntry) {
      return;
    }

    const trySync = () => {
      void syncPending().catch((error) => {
        console.error('Failed to sync queued emulator saves:', error);
      });
    };

    const intervalId = window.setInterval(trySync, syncIntervalMs);
    window.addEventListener('online', trySync);

    if ((typeof navigator === 'undefined' || navigator.onLine) && entries.length > 0) {
      trySync();
    }

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('online', trySync);
    };
  }, [enabled, entries.length, syncEntry, syncIntervalMs, syncPending]);

  return useMemo(
    () => ({
      entries,
      isLoaded,
      isSyncing,
      enqueue,
      remove,
      getEntry,
      loadEntries,
      syncPending,
    }),
    [entries, enqueue, getEntry, isLoaded, isSyncing, loadEntries, remove, syncPending]
  );
};
